import { Card, CardContent } from "@/components/ui/card";
import { ProgressCircleManySegments } from "@/components/ui/charts/progress-circle-many-segments";
import { useCurrentUserProfile } from "@/hooks/current-user-profile.hook";
import { useDimensionByQuizId } from "@/hooks/dimension-by-quiz-id.hook";
import { getDimensionById } from "@/lib/dimensions";
import { DimensionId } from "@/types/questionnaries";
import { useParams } from "react-router-dom";
import SummarySkeleton from "./summary-skeleton";

const Summary = () => {
  const { quizId, dimensionId: dimensionIdParam } = useParams();

  if (!dimensionIdParam) {
    throw new Error("Dimension param not defined");
  }

  const dimensionId = dimensionIdParam as DimensionId;

  const dimension = getDimensionById(dimensionId);

  if (!dimension) {
    throw new Error("Dimension not found");
  }

  const { data: userProfile } = useCurrentUserProfile();

  const { data: dimensionSummary, isLoading: isDimensionSummaryLoading } =
    useDimensionByQuizId({
      companyId: userProfile?.companyId || "",
      applicationQuizId: quizId || "",
      dimension: dimensionId,
    });

  if (isDimensionSummaryLoading) {
    return <SummarySkeleton />;
  }

  if (!dimensionSummary || !dimensionSummary.result) {
    return null;
  }

  return (
    <Card>
      <CardContent className="flex flex-col gap-4 pt-6 xl:flex-row xl:items-start">
        <table className="grow overflow-hidden rounded">
          <thead>
            <tr
              style={{ backgroundColor: dimension.color }}
              className="border-b border-b-white text-center text-lg font-bold text-white"
            >
              <th className="border-r border-r-white py-3">Dimensión</th>
              <th className="border-r border-r-white py-3">Tema</th>
              <th className="border-r border-r-white py-3" colSpan={2}>
                Puntaje
              </th>
            </tr>
          </thead>

          <tbody>
            {dimensionSummary?.topics?.map((topic, index) => (
              <tr key={topic.topic}>
                {index === 0 && (
                  <td
                    rowSpan={dimensionSummary.topicsCount}
                    style={{ backgroundColor: dimension.color }}
                    className="border-r border-r-white py-3 text-center text-lg font-bold text-white"
                  >
                    {dimension.label}
                  </td>
                )}
                <td className="border-b border-r p-3 text-sm">{topic.name}</td>
                <td className="border-b border-r p-3 text-right text-sm">
                  {topic.result ? topic.result.score : "-"}
                </td>
                <td className="border-b border-r p-3 text-right text-sm">
                  {topic.result ? `${topic.result.scorePercentage}%` : "-"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="flex shrink-0 flex-col items-center p-20 xl:self-center xl:py-0">
          <ProgressCircleManySegments
            radius={70}
            strokeWidth={20}
            segments={[
              {
                color: `color-mix(in srgb, ${dimension.color}, white 90%)`,
                value: 100 - dimensionSummary.result.scorePercentage,
              },
              {
                color: dimension.color || "black",
                value: dimensionSummary.result.scorePercentage,
              },
            ]}
          >
            <div className="items-center text-lg font-semibold text-black">
              {dimensionSummary.result.score}
            </div>
          </ProgressCircleManySegments>

          <p className="mt-4 text-2xl font-bold">Tu puntaje</p>
        </div>
      </CardContent>
    </Card>
  );
};

export default Summary;
